import { faX } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Checkbox, Modal } from '@mui/material';
import './styles.scss';
import { useState, useMemo, useEffect } from 'react';

import { CustomWidget, LIST_WIDGETS, WidgetKeys } from '../../../providers/model/custom-widget';

import WidgetSelect from './widget-select';

interface IcreateWidgetModal {
  open: boolean;
  handleClose: () => void;
  handleAddWidget: (widgets: CustomWidget[]) => void;
  isDeleteWidget?: boolean;
  handleDeleteWidget?: (widget: WidgetKeys) => void;
  currentWidget?: string;
  currentWidgets: CustomWidget[];
}

const EXCLUDED_WIDGETS = [WidgetKeys.VolatilitySurfaceMarking];

const WIDGETS_FILTERED = LIST_WIDGETS.filter((widget) => !EXCLUDED_WIDGETS.includes(widget.key));

const HandleWidgetModal = ({
  open,
  handleClose,
  handleAddWidget,
  isDeleteWidget,
  handleDeleteWidget,
  currentWidget,
  currentWidgets,
}: IcreateWidgetModal) => {
  const [selectedWidgets, setSelectedWidgets] = useState<WidgetKeys[]>([]);

  const selectAll = useMemo(() => {
    return selectedWidgets.length === WIDGETS_FILTERED.length;
  }, [selectedWidgets]);

  const onCheck = (widget: WidgetKeys, checked: boolean) => {
    setSelectedWidgets((prev) => (checked ? [...prev, widget] : prev.filter((w) => w !== widget)));
  };

  const onSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedWidgets(WIDGETS_FILTERED.map((widget) => widget.key));
    } else {
      setSelectedWidgets([]);
    }
  };

  const handleSave = () => {
    if (currentWidgets.length > 0) {
      const selectedWidgetsOrdered = selectedWidgets
        .map((key) => {
          const widget = WIDGETS_FILTERED.find((w) => w.key === key);
          if (!widget) return null;

          const baseId = widget.id ?? widget.key;
          const randomId = Math.floor(Math.random() * 10000);

          return {
            ...widget,
            id: `${baseId}_${randomId}`,
          } as CustomWidget;
        })
        .filter((widget): widget is CustomWidget => widget !== null);

      const allWidgets = [...currentWidgets, ...selectedWidgetsOrdered];
      const sortedWidgets = allWidgets.sort((a, b) => a.id!.localeCompare(b.id!));

      handleAddWidget(sortedWidgets);
    } else {
      handleAddWidget(WIDGETS_FILTERED.filter((widget) => selectedWidgets.includes(widget.key)));
    }
    handleClose();
  };

  const deleteWidget = () => {
    if (handleDeleteWidget) {
      handleDeleteWidget(currentWidget as WidgetKeys);
    }
    handleCloseWrapper();
  };

  const getTitle = () => {
    if (isDeleteWidget) {
      return 'Are you sure you want to remove this widget?';
    }
    return 'Customize your tab';
  };

  const renderButtons = () => {
    if (isDeleteWidget) {
      return (
        <div className="buttons">
          <Button className="cancel-button" onClick={handleCloseWrapper}>
            Cancel
          </Button>
          <Button className="delete-button" onClick={deleteWidget}>
            Delete anyway
          </Button>
        </div>
      );
    }

    const button = {
      name: `save`,
      className: 'add-button',
      fn: () => handleSave(),
    };

    return (
      <div className="buttons">
        <Button className="cancel-button" onClick={handleCloseWrapper}>
          Cancel
        </Button>
        <Button className={button.className} onClick={button.fn} disabled={!selectedWidgets.length}>
          {button.name}
        </Button>
      </div>
    );
  };

  const renderWidgets = () => {
    const isIndeterminate =
      selectedWidgets.length > 0 && selectedWidgets.length < WIDGETS_FILTERED.length;

    return (
      <div className="widgets-container">
        <div className="widgets-container-header">
          <label className={`checkbox-select-all`}>
            <Checkbox
              checked={selectAll}
              onChange={(e) => onSelectAll(e.target.checked)}
              indeterminate={isIndeterminate}
            />
            <span className="label">Select All</span>
          </label>
        </div>
        <div className="widgets-items">
          {WIDGETS_FILTERED.map((widget) => (
            <WidgetSelect
              key={widget.key}
              widget={widget}
              isChecked={selectedWidgets.includes(widget.key)}
              onCheck={(checked) => onCheck(widget.key, checked)}
            />
          ))}
        </div>
      </div>
    );
  };

  const handleCloseWrapper = () => {
    handleClose();
  };

  useEffect(() => {
    if (!currentWidgets) return;

    setSelectedWidgets([]);
  }, [currentWidgets]);

  const title = getTitle();

  const classNameContainer = `handle-widget-modal-content ${isDeleteWidget ? 'delete-widget' : ''}`;

  return (
    <Modal open={open} onClose={handleCloseWrapper} className="modal-container-class">
      <div className="handle-widget-modal-container">
        <div className={classNameContainer}>
          <div className="header">
            <div className="header-titles">
              <h1>{title}</h1>
              {!isDeleteWidget && <span>Select the metrics to add to your board</span>}
            </div>

            <Button className="close-button" onClick={handleCloseWrapper}>
              <FontAwesomeIcon icon={faX} className="close-button-icon" />
            </Button>
          </div>
          {!isDeleteWidget && renderWidgets()}
          {renderButtons()}
        </div>
      </div>
    </Modal>
  );
};

export default HandleWidgetModal;
